import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import {
    getNotifications,
    notificationService,
} from "../../utils/FirebaseHelper";
import {
    InfiniteLoader,
    List,
    CellMeasurer,
    CellMeasurerCache,
    AutoSizer,
} from "react-virtualized";
import NotificationItem from "./NotificationItem";
import { isEmpty } from "lodash";
import NotificationsAPI from "../../API/notifications.api";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { getNotificationSound } from "../../utils/SoundsHelper";
import { useNavigate } from "react-router-dom";
import BellIcon from "../../assets/images/bell.png";

const NotificationCenter = ({
    notificationDropdownOpen,
    setNotificationDropdownOpen,
    notifications,
    setNotifications,
    notificationsActive,
    setNotificationsActive,
    userEmail,
}) => {
    const { t } = useTranslation();
    const [lastVisible, setLastVisible] = useState({});
    const [loading, setLoading] = useState(false);
    const isRendered = useRef(false);
    const navigate = useNavigate();

    const notificationAudio = getNotificationSound();

    //Stores the row measurements to avoid recalculate them all the time
    const cache = new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: 100,
    });

    useEffect(() => {
        const NotificationSubscription = notificationService
            .getMessage()
            .subscribe(handleNotificationSubscription);

        () => NotificationSubscription.unsubscribe();
    }, []);

    const handleNotificationSubscription = async (messages) => {
        LogCapture.info("Notification Center: handleNotificationSubscription");
        if (messages.length !== 0) {
            const lastUpdate = await NotificationsAPI.getLastSeen();
            const isNotificationActive =
                new Date(lastUpdate) < new Date(messages[0].timestamp.toDate());
            setNotificationsActive(isNotificationActive);

            isNotificationActive && playNotificationAudio();
            setNotifications((prev) => {
                const newNotifications = messages.filter(
                    (message) =>
                        !prev.find(
                            (item) =>
                                item.notificationId === message.notificationId
                        )
                );
                return [...newNotifications, ...prev];
            });
        }
        if (!isRendered.current) {
            loadMoreRows(isRendered.current);
            isRendered.current = true;
        }
    };

    const handleOpenNotifications = () => {
        setNotificationDropdownOpen(!notificationDropdownOpen);
        setNotificationsActive(false);
        NotificationsAPI.setLastSeen();
        LogCapture.info(
            `User ${userEmail} updated the last seen for notification center`
        );
    };

    const loadMoreRows = () => {
        setLoading(true);
        getNotifications(lastVisible).then((result) => {
            LogCapture.info("Notification Center: loadMoreRows");
            let records = result?.records || [];
            setLastVisible(result?.lastVisible);
            setNotifications((prev) => [...prev, ...records]);
            LogCapture.info(
                `User ${userEmail} loaded notifications successfully`
            );
            setLoading(false);
        });
    };

    const playNotificationAudio = () => {
        notificationAudio
            .play()
            .catch(() => console.log("Notification sound unavailable"));
    };

    const handleLinkClicked = (link) => {
        if (!link) return;
        setNotificationDropdownOpen(false);
        navigate(link);
    };

    const rowRenderer = ({ key, index, style, parent }) => {
        if (!isEmpty(notifications))
            return (
                //Measures the child's height and stores it in the measurer cache
                <CellMeasurer
                    key={key}
                    cache={cache}
                    parent={parent}
                    columnIndex={0}
                    rowIndex={index}
                >
                    {({ registerChild }) => (
                        <div
                            ref={registerChild}
                            style={style}
                            className={`px-4 py-2 ${
                                notifications[index].body?.message?.link &&
                                "hover:bg-secondary-color-lighter-white cursor-pointer"
                            }`}
                            onClick={() =>
                                handleLinkClicked(
                                    notifications[index].body?.message?.link
                                )
                            }
                        >
                            <NotificationItem
                                notification={notifications[index]}
                                handleToggleRead={toggleReadNotification}
                            />
                        </div>
                    )}
                </CellMeasurer>
            );
    };

    const toggleReadNotification = (id) => {
        setNotifications(
            (prevNotifications) =>
                prevNotifications &&
                prevNotifications.map((notification) => {
                    if (notification.id === id) {
                        notification.isUnread = !notification.isUnread;
                    }
                    return notification;
                })
        );
    };

    const isRowLoaded = ({ index }) => {
        return !!notifications[index];
    };

    const noRowsRenderer = () => {
        return (
            <div className="px-4 py-2 h-full flex flex-col justify-center items-center gap-3">
                <img width={80} height={80} src={BellIcon} />
                <p className="text-center">{t("emptyNotifications")}</p>
            </div>
        );
    };

    return (
        <div
            className="notification-wrapper mr-5 relative"
            id="notification-center"
        >
            <i
                className="fas fa-bell notification-icon-active relative text-primary-color-blue cursor-pointer fa-lg"
                data-before={notificationsActive}
                onClick={handleOpenNotifications}
            />
            <div
                className={`notification-menu notification ${
                    notificationDropdownOpen ? "show" : ""
                }`}
            >
                <div className="prod-title product-info-what-new px-4 py-3">
                    <h3 className="m-0">{t("notifications")}</h3>
                </div>
                <hr />
                <InfiniteLoader
                    isRowLoaded={isRowLoaded}
                    loadMoreRows={loadMoreRows}
                    rowCount={100}
                    threshold={2}
                >
                    {({ onRowsRendered, registerChild }) => (
                        <>
                            <div className="h-64">
                                {/* AutoSizer is a HOC which provides the automatic calculation of height to the component */}
                                <AutoSizer>
                                    {({ height }) => (
                                        <List
                                            deferredMeasurementCache={cache}
                                            height={height}
                                            onRowsRendered={onRowsRendered}
                                            ref={registerChild}
                                            rowCount={
                                                notifications?.length || 0
                                            }
                                            rowHeight={cache.rowHeight}
                                            rowRenderer={rowRenderer}
                                            noRowsRenderer={noRowsRenderer}
                                            width={400}
                                            className="scroll-y"
                                        />
                                    )}
                                </AutoSizer>
                            </div>
                            {loading && (
                                <div
                                    className="uppercase text-xxs text-secondary-color-lighter-blue"
                                    style={{ textAlign: "center" }}
                                >
                                    Loading...
                                </div>
                            )}
                        </>
                    )}
                </InfiniteLoader>
            </div>
        </div>
    );
};

export default NotificationCenter;
